import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import { StatusCode } from "api/protocols";
import { DeleteEmailMarketingTemplate } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplates";
import type { IEmailMarketingTemplateListItem } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplatesTypes";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";

type TEmailBuilderTemplateLibraryDeleteModalProps = {
  open: boolean;
  template: IEmailMarketingTemplateListItem | null;
  onClose: () => void;
  onDeleted: (templateId: number) => void;
  onError: (message: string) => void;
};

function apiErrorMessage(message: unknown, fallback: string): string {
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  return fallback;
}

export function EmailBuilderTemplateLibraryDeleteModal(
  props: TEmailBuilderTemplateLibraryDeleteModalProps,
) {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const isMobile = (windowSize.width ?? MOBILE_WIDTH + 1) <= MOBILE_WIDTH;
  const [modalState, setModalState] = useState({
    open: false,
    deleting: false,
  });
  if (props.open !== modalState.open) {
    setModalState({
      open: props.open,
      deleting: props.open ? false : modalState.deleting,
    });
  }
  const deleting = modalState.deleting;
  const setDeleting = (nextDeleting: boolean) => {
    setModalState(current => ({ ...current, deleting: nextDeleting }));
  };
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    const active = window.document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;
  }, [props.open]);

  useEffect(() => {
    if (props.open) {
      return;
    }

    const returnFocus = returnFocusRef.current;
    if (returnFocus !== null && typeof returnFocus.focus === "function") {
      returnFocus.focus();
    }
    returnFocusRef.current = null;
  }, [props.open]);

  useEffect(() => {
    if (!props.open || deleting) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
      }
    }

    window.document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.open, props.onClose, deleting]);

  function handleClose() {
    if (deleting) {
      return;
    }

    props.onClose();
  }

  async function handleDelete() {
    if (props.template === null || deleting) {
      return;
    }

    setDeleting(true);

    try {
      const res = await DeleteEmailMarketingTemplate(props.template.id, false);
      if (res.status !== StatusCode.OK) {
        props.onError(apiErrorMessage(res.message, t("Could not delete template.")));
        return;
      }

      props.onDeleted(props.template.id);
      props.onClose();
    } finally {
      setDeleting(false);
    }
  }

  const description = t(
    "Are you sure you want to delete {{name}}? Existing campaigns that used this template will not be affected.",
    { name: props.template?.name ?? "" },
  );

  if (isMobile) {
    return (
      <Sheet
        open={props.open}
        size="small"
        title={t("Delete template")}
        supportText={description}
        closable
        onCancel={handleClose}
        cancelText={t("Cancel")}
        okText={t("Delete")}
        onOk={() => {
          void handleDelete();
        }}
        okLoading={deleting}
        okDisabled={deleting || props.template === null}
        cancelDisabled={deleting}
        backDropCancel={!deleting}
      />
    );
  }

  return (
    <Popup
      open={props.open}
      type="warning"
      title={t("Delete template")}
      description={description}
      closable
      onCancel={handleClose}
      cancelText={t("Cancel")}
      okText={deleting ? t("Deleting…") : t("Delete")}
      onOk={() => {
        void handleDelete();
      }}
      okDisabled={deleting || props.template === null}
      cancelDisabled={deleting}
      backDropCancel={!deleting}
    />
  );
}
